import { useEffect, type JSX, type ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth';

/**
 * Authentication boundary (H3.1 round-4).
 *
 * When the user hits a protected route without a token, redirect them
 * to `/login`. Public surfaces (`/login` itself, and historically `/`
 * landing copy) remain accessible.
 *
 * Round-3 shipped this as a passthrough that emitted a "Signed out"
 * notice. Round-4 replaces it with a real gate so the PM/PO journey
 * forces a sign-in before any tenant-scoped surface renders.
 */
const PUBLIC_PATHS = new Set<string>(['/login', '/onboarding']);

export function AuthBoundary({ children }: { children: ReactNode }): JSX.Element {
  const hydrated = useAuthStore((s) => s.hydrated);
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hydrated) return;
    const pathname = location.pathname;
    const isPublic = PUBLIC_PATHS.has(pathname);
    if (!token && !isPublic) {
      navigate({ to: '/login', replace: true }).catch(() => {
        /* navigation aborted; ignore */
      });
    }
  }, [hydrated, token, location.pathname, navigate]);

  if (!hydrated) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="grid min-h-screen place-items-center bg-bg-base text-fg-muted"
      >
        <span className="shakti-mono text-xs uppercase tracking-wider">
          Restoring session…
        </span>
      </div>
    );
  }

  return <>{children}</>;
}
